import { Option } from '../types';
import uniqBy from './uniq-by';

export const RECENT_SEARCHES_KEY = 'recentSearches';
export const MAX_RECENT_SEARCHES = 5;

// An in-memory Storage used when the real Web Storage is unavailable (e.g.
// storage disabled, Safari private mode) so callers never crash on access.
const createNoopStorage = (): Storage => {
  const store = new Map<string, string>();
  return {
    getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
    setItem: (key, value) => {
      store.set(key, String(value));
    },
    removeItem: (key) => {
      store.delete(key);
    },
    clear: () => {
      store.clear();
    },
    key: (index) => Array.from(store.keys())[index] ?? null,
    get length() {
      return store.size;
    },
  } as Storage;
};

/**
 * Returns the requested Web Storage ('local' | 'session'), falling back to an
 * in-memory no-op Storage when accessing it throws (blocked/unavailable).
 */
export const getSafeStorage = (type: 'local' | 'session'): Storage => {
  try {
    const storage =
      type === 'local' ? window.localStorage : window.sessionStorage;
    // Read-only probe: only bail out if the storage object is truly
    // inaccessible. Don't probe with a write — storage that reads but blocks
    // writes (quota full, private mode) should still be returned so existing
    // searches stay readable; saveRecentSearches already guards writes.
    storage.getItem('__recent_searches_probe__');
    return storage;
  } catch {
    return createNoopStorage();
  }
};

export const getRecentSearches = (storage: Storage): Option[] => {
  try {
    const stored = storage.getItem(RECENT_SEARCHES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveRecentSearches = (
  storage: Storage,
  existing: Option[],
  selected: Option[],
  max?: number,
): Option[] => {
  const selectedValues = new Set(selected.map((item) => item.value));
  const deduped: Option[] = uniqBy(
    existing.filter((item) => !selectedValues.has(item.value)).concat(selected),
    (item: { value: string }) => item.value,
  );

  const capped =
    typeof max === 'number' && max > 0 ? deduped.slice(-max) : deduped;

  try {
    storage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(capped));
  } catch {
    // Persistence can fail (quota exceeded, Safari private mode, storage
    // blocked); the search flow should still proceed with the in-memory list.
  }

  return capped;
};
